import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { Button } from '../components/Button'
import { AppScreensCarousel, V2_SCREENS } from '../components/AppScreensCarousel'
import { openCheckout, getPaddlePriceIds } from '../lib/paddle'
import {
  trackCheckoutStarted,
  savePurchaseData,
  firePurchaseConversion,
  trackMetaInitiateCheckout,
  fireMetaCheckoutInitiated,
  fireMetaPurchase,
  fireMetaStartTrial,
  newMetaEventId,
  trackEvent,
} from '../lib/analytics'
import { isMetaVariant, getVariant, usesAppLook, isShortVariant } from '../lib/variant'
import { V2Icon } from '../components/v2'
import { loopsPurchaseCompleted } from '../lib/loops'
import { calculateNutrientLimits } from '../lib/nutrients'

const plans = [
  { id: '1month', name: '1 Month', price: 14.99, perDay: 0.50, months: 1, popular: false, trial: false, renewalPeriod: 'every month', renewalPrice: '$14.99' },
  { id: '3month', name: '3 Months', price: 29.99, perDay: 0.33, months: 3, popular: false, trial: false, renewalPeriod: 'every 3 months', renewalPrice: '$29.99' },
  { id: '1year', name: '1 Year', price: 59.99, perDay: 0.16, months: 12, popular: true, trial: true, renewalPeriod: 'every year', renewalPrice: '$59.99' },
]

// Real customer words, quoted word for word (… marks a cut). Store reviews carry their real
// star rating; emails we were sent have no rating, so they show no stars. No lab, stage or
// "saved my life" results: a testimonial's result counts as our own claim. Traced 2026-10-21
// (App Store Connect API, Play Console exports, the boss man's saved-feedback list).
interface OfferTestimonial {
  name: string
  text: string
  /** Store star rating. Leave unset for an email: no stars shown. */
  rating?: number
  /** Excludes from meta variants (kept in organic only) for ad-compliance margin. */
  outcomeClaim?: boolean
}

const testimonials: OfferTestimonial[] = [
  { name: 'Joyce', text: "This app is EVERYTHING! … You have taken out the stress of figuring out each meal and helped break down my meals. I am no longer fighting with my children over what I'm allowed and what I'm not allowed 😂" }, // email
  { name: 'Doug', text: "…This app has helped with tracking no end, use it all the time. … Its worth the money to me.", rating: 5 }, // Play 2026-10-07
  { name: 'Michael', text: "…when I showed it to my doctor he never heard of it and he took it to give to other patients…", rating: 5 }, // Play 2026-06-25
  { name: 'Pam', text: "New to this app, however I am truly loving all that it offers. It's taking all the guess-work out of my new reality.", rating: 5 }, // Play 2026-09-06
  { name: 'Ron', text: "I just want to let you know how much I like your system. It is extremely helpful in managing my diet. Thank you for a great dietary tool!" }, // email
  { name: 'Joan K.', text: "This is great. Much better than looking up food values and writing everything. This app does so much more.", rating: 5 }, // Play 2026-09-21
  { name: 'Scott West', text: "This app is easily worth every penny paid. It simplifies the management of my CKD.", rating: 5 }, // Play 2026-09-12
]

// The honest anchor: what the same months cost on our own monthly plan (a real, sold price),
// never an invented "was" price. Rounded down so the claim is never bigger than the math.
const MONTHLY_PRICE = plans.find((p) => p.months === 1)!.price
const monthlyTotal = (months: number) => MONTHLY_PRICE * months
const savingsVsMonthly = (plan: { price: number; months: number }) =>
  plan.months > 1 ? Math.floor((1 - plan.price / monthlyTotal(plan.months)) * 100) : 0

// Paddle (merchant of record) adds US sales tax / VAT on top for some buyers, so never say "tax included".
const TAX_NOTE = 'Sales tax may be added at checkout, depending on where you live.'

const kidneyStatusLabels: Record<string, string> = {
  stage_1: 'CKD Stage 1', stage_2: 'CKD Stage 2', stage_3a: 'Stage 3a', stage_3b: 'Stage 3b',
  stage_4: 'CKD Stage 4', stage_5: 'CKD Stage 5', on_dialysis: 'Dialysis',
  kidney_stones: 'Kidney Stones', no_diagnosis: 'Monitoring', not_sure: 'Kidney Health',
}

const FEATURES = [
  { icon: '📸', v2Icon: 'feature-scan', text: 'Unlimited AI Scans' },
  { icon: '🍽️', v2Icon: 'feature-recipes', text: '100+ Kidney Recipes' },
  { icon: '📊', v2Icon: 'feature-tracking', text: 'Nutrient Tracking' },
  { icon: '🩺', v2Icon: 'feature-report', text: "Doctor's Report PDF" },
  { icon: '💬', v2Icon: 'feature-chat', text: 'Unlimited AI Chat' },
  { icon: '📈', v2Icon: 'feature-trends', text: 'Trends & Insights' },
]

// Current app feature set (4.32–4.41: label/menu scans, stage limits, labs, binders, heatmap).
const V2_FEATURES = [
  { icon: '📸', v2Icon: 'feature-scan', text: 'AI Meal & Label Scans' },
  { icon: '📊', v2Icon: 'feature-tracking', text: 'Limits for Your Stage' },
  { icon: '🧪', v2Icon: 'feature-labs', text: 'Labs & Vitals Tracking' },
  { icon: '🩺', v2Icon: 'feature-report', text: "Doctor's Report PDF" },
  { icon: '💬', v2Icon: 'feature-chat', text: 'Unlimited AI Chat' },
  { icon: '🍽️', v2Icon: 'feature-recipes', text: '100+ Recipes + Import' },
  { icon: '💧', v2Icon: 'fluid-water', text: 'Fluid & Binder Tracking' },
  { icon: '📈', v2Icon: 'feature-trends', text: 'Trends & Day Heatmap' },
]

export function OfferStep() {
  const { goNext, data, updateData } = useOnboardingStore()
  // App look: illustrated marks for the nutrient row + feature list, current features + 4.41 screenshots.
  const v2 = usesAppLook()
  // Part B offer_v2 (meta-short only): one annual free-trial card; "See other plans" reveals the grid.
  const offerV2 = isShortVariant()
  const [showAllPlans, setShowAllPlans] = useState(false)
  const paddlePriceIds = useMemo(() => getPaddlePriceIds(), [])
  const [selectedPlan, setSelectedPlan] = useState('1year')
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [isCheckingOut, setIsCheckingOut] = useState(false)

  // On meta variants, drop testimonials with outcome/efficacy claims (none today).
  const variantTestimonials = isMetaVariant()
    ? testimonials.filter((t) => !t.outcomeClaim)
    : testimonials

  const limits = calculateNutrientLimits(data.kidneyStatus, data.comorbidities, data.weightKg, data.dialysisType)
  const stageLabel = kidneyStatusLabels[data.kidneyStatus || 'not_sure'] || 'Kidney Health'

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % variantTestimonials.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const selectedPlanData = plans.find(p => p.id === selectedPlan)!
  const renewalLine = selectedPlanData.trial
    ? `Free for 7 days, then ${selectedPlanData.renewalPrice}/year. We'll email you before your trial ends. Renews automatically. Cancel anytime.`
    : `${selectedPlanData.renewalPrice} ${selectedPlanData.renewalPeriod}. Renews automatically. Cancel anytime.`

  const handleCheckout = async () => {
    const priceId = paddlePriceIds[selectedPlan]

    if (!priceId) {
      console.warn('No Paddle price ID configured, skipping payment')
      goNext()
      return
    }

    if (!data.email) {
      console.error('No email provided')
      return
    }

    setIsCheckingOut(true)
    trackCheckoutStarted(selectedPlan, selectedPlanData.price)
    trackMetaInitiateCheckout(selectedPlan, selectedPlanData.price)
    fireMetaCheckoutInitiated(selectedPlan, selectedPlanData.price)

    // Generate the Meta dedup event ID up front. Same value fires browser-side
    // on Paddle success AND server-side from paddleWebhook.ts CAPI.
    const metaPurchaseEventId = newMetaEventId()
    updateData({ metaPurchaseEventId, funnelVariant: getVariant() })

    try {
      await openCheckout({
        priceId,
        planId: selectedPlan,
        email: data.email,
        metaPurchaseEventId,
        onSuccess: async (transactionId) => {
          savePurchaseData(transactionId, selectedPlan, selectedPlanData.price, selectedPlanData.trial, data.email || undefined)

          // Trial plans (1yr): fire StartTrial + kp_event_09 only — no money
          // has changed hands yet. The real Purchase event fires server-side
          // from the Meta CAPI outbox when the first real charge clears
          // (~7 days after trial start).
          // Non-trial plans (1mo, 3mo): fire Purchase immediately — Paddle
          // charges the card at checkout.
          if (selectedPlanData.trial) {
            fireMetaStartTrial(metaPurchaseEventId, selectedPlan, selectedPlanData.price)
          } else {
            fireMetaPurchase(metaPurchaseEventId, selectedPlan, selectedPlanData.price)
          }

          // GA4 funnel_trial_started / funnel_immediate_purchase; Google Ads
          // conversion for immediate purchases only (plan §A3).
          await firePurchaseConversion(transactionId, selectedPlan, selectedPlanData.price, selectedPlanData.trial, data.email || undefined)

          if (data.email) {
            loopsPurchaseCompleted(data.email, selectedPlan, transactionId)
          }

          updateData({
            paddleTransactionId: transactionId,
            purchasePlanId: selectedPlan,
            purchasePriceUsd: selectedPlanData.price,
          })
          goNext()
        },
        onClose: () => {
          setIsCheckingOut(false)
        },
      })
    } catch (error) {
      console.error('Checkout error:', error)
      setIsCheckingOut(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-6 py-6">

          {/* ──── HERO: Title + Compact Nutrient Budget ──── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-5">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              Your personalized <span className="text-primary">{stageLabel}</span> plan is ready
            </h1>
          </motion.div>

          {/* Compact nutrient row */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            {[
              { emoji: '🧂', img: 'sodium', label: 'Sodium', value: limits.sodium, unit: 'mg', bg: 'rgba(245, 158, 11, 0.10)' },
              { emoji: '⚡', img: 'potassium', label: 'Potassium', value: limits.potassium, unit: 'mg', bg: 'rgba(34, 197, 94, 0.10)' },
              { emoji: '🦴', img: 'phosphorus', label: 'Phosphorus', value: limits.phosphorus, unit: 'mg', bg: 'rgba(139, 92, 246, 0.10)' },
              { emoji: '💪', img: 'protein', label: 'Protein', value: limits.protein, unit: 'g', bg: 'rgba(59, 130, 246, 0.10)' },
            ].map((n) => (
              <div key={n.label} className="rounded-xl p-2.5 text-center" style={{ backgroundColor: n.bg }}>
                {v2 ? (
                  <img src={`/images/nutrients/${n.img}.png`} alt="" className="w-5 h-5 object-contain mx-auto" />
                ) : (
                  <span className="text-sm">{n.emoji}</span>
                )}
                <p className="text-[10px] font-medium text-gray-500 mt-0.5">{n.label}</p>
                <p className="font-bold text-sm text-gray-900">{n.value.toLocaleString()}</p>
                <p className="text-[10px] text-gray-500">{n.unit}</p>
              </div>
            ))}
          </div>

          {/* ──── PRICING PLANS (above the fold) ──── */}
          {offerV2 && !showAllPlans ? (
            <div className="mb-4">
              <div className="relative p-5 rounded-2xl border-2 border-primary bg-teal-50">
                <span className="absolute -top-3 right-4 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">
                  7-DAY FREE TRIAL
                </span>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-lg text-gray-900">KidneyPal Pro · 1 Year</p>
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-gray-900">$59.99/year</span>
                    </p>
                    <p className="text-xs text-gray-500">$0.16 per day</p>
                    <p className="text-xs font-semibold text-primary mt-1">
                      Save {savingsVsMonthly(plans.find((p) => p.id === '1year')!)}% vs paying monthly (${monthlyTotal(12).toFixed(2)}/year)
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-2xl text-gray-900">$0</p>
                    <p className="text-sm text-gray-500">due today</p>
                  </div>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  <li className="flex gap-2"><span className="text-primary font-bold">✓</span>Free for 7 days, then $59.99 a year</li>
                  <li className="flex gap-2"><span className="text-primary font-bold">✓</span>Cancel before day 7 and you won't be charged</li>
                </ul>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowAllPlans(true)
                  trackEvent('funnel_offer_other_plans_opened')
                }}
                className="block mx-auto mt-3 text-sm text-gray-500 underline"
              >
                See other plans
              </button>
            </div>
          ) : (
          <>
          <h3 className="text-lg font-bold text-center text-gray-900 mb-3">Choose your plan</h3>
          <div className="space-y-3 mb-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedPlan === plan.id ? 'border-primary bg-teal-50' : 'border-gray-200 bg-white'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-4 bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                    MOST POPULAR
                  </span>
                )}
                {plan.trial && (
                  <span className="absolute -top-3 right-4 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">
                    7-DAY FREE TRIAL
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === plan.id ? 'border-primary' : 'border-gray-300'
                    }`}>
                      {selectedPlan === plan.id && <div className="w-3 h-3 rounded-full bg-primary" />}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{plan.name}</p>
                      <p className="text-sm text-gray-500">
                        <span className="font-semibold text-gray-900">${plan.price.toFixed(2)}</span>
                        {savingsVsMonthly(plan) > 0 && (
                          <span className="ml-2 text-xs font-semibold text-primary">Save {savingsVsMonthly(plan)}% vs monthly</span>
                        )}
                      </p>
                      {plan.trial && (
                        <p className="text-xs font-semibold text-amber-600 mt-0.5">Try free for 7 days</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">${plan.perDay.toFixed(2)}</p>
                    <p className="text-sm text-gray-500">per day</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
          )}

          {/* Primary CTA */}
          <Button onClick={handleCheckout} disabled={isCheckingOut}>
            {isCheckingOut ? 'Processing...' : selectedPlanData.trial ? 'Start my free trial' : 'Get my plan'}
          </Button>

          {/* Renewal terms next to the button (auto-renewal laws want them beside the consent, not below the fold) */}
          <p className="text-center text-sm text-gray-600 mt-3">{renewalLine}</p>

          {/* Money-back one-liner */}
          <p className="text-center text-sm text-gray-500 mt-1 mb-6">
            <span className="font-semibold text-gray-700">30-day money-back guarantee</span> from your first payment
          </p>

          {/* ──── FEATURES (compact, 2-column grid) ──── */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-6">
            {(v2 ? V2_FEATURES : FEATURES).map((f) => (
              <div key={f.text} className="flex items-center gap-2">
                {v2 ? <V2Icon name={f.v2Icon} size={24} /> : <span className="text-base shrink-0">{f.icon}</span>}
                <span className="text-sm font-medium text-gray-800">{f.text}</span>
              </div>
            ))}
          </div>

          {/* ──── TESTIMONIAL ──── */}
          <div className="bg-teal-50 rounded-2xl p-5 mb-6">
            {variantTestimonials[currentTestimonial].rating && (
              <div className="flex gap-0.5 mb-2">
                {[...Array(variantTestimonials[currentTestimonial].rating)].map((_, i) => <span key={i} className="text-amber-400 text-sm">★</span>)}
              </div>
            )}
            <p className="text-gray-700 text-sm mb-2">"{variantTestimonials[currentTestimonial].text}"</p>
            <p className="font-semibold text-gray-900 text-sm">— {variantTestimonials[currentTestimonial].name}</p>
            <div className="flex justify-center gap-2 mt-3">
              {variantTestimonials.map((_, i) => (
                <button key={i} onClick={() => setCurrentTestimonial(i)} className={`w-2 h-2 rounded-full ${i === currentTestimonial ? 'bg-gray-900' : 'bg-gray-300'}`} />
              ))}
            </div>
          </div>

          {/* ──── APP SCREENSHOTS ──── */}
          <AppScreensCarousel screens={v2 ? V2_SCREENS : undefined} />

          {/* ──── CHECKOUT SUMMARY + SECOND CTA ──── */}
          <div className="mt-8 mb-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Your personalized kidney plan</span><span className="text-gray-600">${selectedPlanData.price.toFixed(2)}</span></div>
              {selectedPlanData.trial && (
                <div className="flex justify-between text-amber-600"><span>7-day free trial</span><span>-${selectedPlanData.price.toFixed(2)}</span></div>
              )}
              <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-lg">
                <span>Due today</span>
                <span>{selectedPlanData.trial ? '$0.00' : `$${selectedPlanData.price.toFixed(2)}`}</span>
              </div>
              {selectedPlanData.trial && (
                <p className="text-xs text-gray-500 text-center">Then ${selectedPlanData.price.toFixed(2)}/year after your free trial</p>
              )}
              <p className="text-xs text-gray-500 text-center">{TAX_NOTE}</p>
            </div>
          </div>

          <Button onClick={handleCheckout} disabled={isCheckingOut} className="mb-3">
            {isCheckingOut ? 'Processing...' : selectedPlanData.trial ? 'Start my free trial' : 'Get my plan'}
          </Button>
          <p className="text-center text-sm text-gray-600 mb-3">{renewalLine}</p>

          <p className="text-xs text-gray-400 text-center mb-8">
            {selectedPlanData.trial
              ? <>After your 7-day free trial, you'll be charged {selectedPlanData.renewalPrice} {selectedPlanData.renewalPeriod} until you cancel. Cancel anytime during your trial and you won't be charged. Email <a href="mailto:support@kidneypal.app" className="underline">support@kidneypal.app</a> or <a href="https://kidneypal.app/account" className="underline">manage your subscription</a>.</>
              : <>Your subscription will auto-renew at {selectedPlanData.renewalPrice} {selectedPlanData.renewalPeriod} until you cancel. To avoid being charged, cancel at least 1 day before your renewal date by emailing <a href="mailto:support@kidneypal.app" className="underline">support@kidneypal.app</a> or by <a href="https://kidneypal.app/account" className="underline">managing your subscription</a>.</>
            }
          </p>

          {/* ──── MONEY BACK GUARANTEE ──── */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-6 text-center">
            <div className="flex justify-center mb-3">
              <img src="/images/moneyback.webp" alt="30-Day Money-Back Guarantee" className="w-20 h-20 object-contain" />
            </div>
            <h4 className="text-lg font-bold text-gray-800 mb-2">30-Day Money-Back Guarantee</h4>
            <p className="text-sm text-gray-600">
              If you're not completely satisfied, let us know within 30 days of your first payment and <strong className="text-gray-800">we'll provide a full refund — no questions asked</strong>.{' '}
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="underline">Refund Policy</a>
            </p>
          </div>

          {/* Trust signals - compact */}
          <div className="flex items-center justify-center gap-6 text-xs text-gray-400 mb-8">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              <span>Secure checkout</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              <span>Data protected</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              <span>support@kidneypal.app</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
